import { authenticatedFetch } from '../api';

export type CatalogSetupLocation = {
  id: string;
  name: string;
  company_id: string;
  company_name: string;
  active_catalog_count: number;
};

export type CatalogSetupCatalog = {
  id: string;
  title: string;
  status: string;
  location_count: number;
};

export type CatalogSetupState = {
  shop_domain: string;
  write_allowed: boolean;
  write_products_granted: boolean;
  locations: CatalogSetupLocation[];
  catalogs: CatalogSetupCatalog[];
};

export type CatalogSetupInput = {
  title: string;
  company_location_ids: string[];
};

export type CatalogSetupPreview = {
  operation: 'create_company_location_catalog';
  shop_domain: string;
  title: string;
  status: 'ACTIVE';
  company_location_ids: string[];
  company_location_count: number;
  product_visibility: 'sales_channel';
  price_list: null;
  publication: null;
  write_allowed: boolean;
  write_products_granted: boolean;
  changes_shopify: true;
};

export type CatalogSetupApplyResult = {
  status: 'applied';
  catalog: {
    id: string;
    title: string;
    status: string;
    company_location_ids: string[];
  };
  sync: {
    status: 'queued' | 'already_queued';
    job_id: string | null;
  };
};

async function jsonOrError<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(body?.detail ?? `${fallback} (${response.status})`);
  }
  return (await response.json()) as T;
}

export async function getCatalogSetupState(): Promise<CatalogSetupState> {
  const response = await authenticatedFetch('/api/commerce/catalog-setup');
  return jsonOrError(response, 'Could not load catalog setup');
}

export async function previewCatalogSetup(
  input: CatalogSetupInput,
): Promise<CatalogSetupPreview> {
  const response = await authenticatedFetch('/api/commerce/catalog-setup/preview', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return jsonOrError(response, 'Could not preview catalog setup');
}

export async function applyCatalogSetup(
  input: CatalogSetupInput,
): Promise<CatalogSetupApplyResult> {
  const response = await authenticatedFetch('/api/commerce/catalog-setup/apply', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return jsonOrError(response, 'Could not apply catalog setup');
}

type ShopifyScopesApi = {
  request(scopes: string[]): Promise<unknown>;
};

type ShopifyGlobal = {
  scopes?: ShopifyScopesApi;
};

export async function requestCatalogWriteScope(): Promise<void> {
  const shopify = (globalThis as typeof globalThis & { shopify?: ShopifyGlobal }).shopify;
  if (!shopify?.scopes) {
    throw new Error('Shopify optional scope controls are unavailable in this app session.');
  }
  await shopify.scopes.request(['write_products']);
}
